import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { PORTFOLIO_GROUPS } from "../data/projects";

export const SectionHeading = ({ eyebrow, title, light = false }) => (
  <div className="mb-14 text-center">
    <span className="mb-3 block text-xs font-medium uppercase tracking-[0.3em] text-accent">{eyebrow}</span>
    <h2 className={`text-3xl font-semibold tracking-tight md:text-4xl ${light ? "text-white" : "text-[#212529]"}`}>
      {title}
    </h2>
    <span className="mx-auto mt-4 block h-0.5 w-12 bg-accent" />
  </div>
);

const Card = ({ item, video, index, testid }) => (
  <motion.a
    data-testid={testid}
    href={item.url}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.8, delay: (index % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
    className="group relative mb-5 block break-inside-avoid overflow-hidden"
  >
    <img
      src={item.image}
      alt={item.title}
      loading="lazy"
      className={`w-full transition-transform duration-700 ease-out group-hover:scale-105 ${video ? "aspect-video object-cover" : ""}`}
    />
    {video && (
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/55 transition-all duration-500 group-hover:scale-0">
          <Play className="ml-1 h-6 w-6 fill-white text-white" />
        </span>
      </span>
    )}
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-accent/90 px-6 text-center opacity-0 transition-opacity duration-500 group-hover:opacity-100">
      <ArrowUpRight className="h-6 w-6 text-white" />
      <h4 className="text-lg font-semibold text-white">{item.title}</h4>
      <span className="text-xs uppercase tracking-[0.2em] text-white/75">{item.category}</span>
    </div>
  </motion.a>
);

const Group = ({ group }) => (
  <div data-testid={`portfolio-group-${group.id}`} className="mb-20 last:mb-0">
    <div className="mb-8 flex items-end justify-between border-b border-[#212529]/10 pb-4">
      <div>
        <h3 className="text-2xl font-semibold tracking-tight text-[#212529] md:text-3xl">{group.heading}</h3>
        <span className="mt-1 block text-xs uppercase tracking-[0.25em] text-[#6c757d]">{group.sub}</span>
      </div>
      <span className="shrink-0 text-sm font-medium text-accent">
        ({String(group.items.length).padStart(2, "0")})
      </span>
    </div>
    <div className="columns-1 gap-5 sm:columns-2 lg:columns-3">
      {group.items.map((item, i) => (
        <Card
          key={item.title}
          item={item}
          video={group.video}
          index={i}
          testid={`portfolio-item-${group.id}-${i}`}
        />
      ))}
    </div>
  </div>
);

export const Portfolio = () => (
  <section id="portfolio" data-testid="portfolio-section" className="bg-white py-24">
    <div className="mx-auto max-w-6xl px-6">
      <SectionHeading eyebrow="Selected Works" title="My Portfolio" />
      {PORTFOLIO_GROUPS.map((g) => (
        <Group key={g.id} group={g} />
      ))}
      <div className="mt-14 text-center">
        <a
          data-testid="portfolio-behance-btn"
          href="https://www.behance.net/travelpartner"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block rounded-full border border-[#212529]/30 px-8 py-3 text-sm font-medium uppercase tracking-wider text-[#212529] transition-all duration-400 hover:border-accent hover:bg-accent hover:text-white"
        >
          View All on Behance
        </a>
      </div>
    </div>
  </section>
);
